import React, { useState, useEffect } from 'react';
import { studentAPI } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const NotificationPreferences = () => {
    const { t } = useLanguage();
    const [preferences, setPreferences] = useState({
        email: '',
        phone: '',
        whatsapp_enabled: false,
        email_notifications: true,
        in_app_notifications: true,
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [testingSendingChannel, setTestingSendingChannel] = useState(null);

    useEffect(() => {
        loadPreferences();
    }, []);

    const loadPreferences = async () => {
        try {
            const response = await studentAPI.getNotificationPreferences?.();
            if (response?.data?.preferences) {
                setPreferences(response.data.preferences);
            }
        } catch (error) {
            console.error('Failed to load preferences:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setPreferences(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await studentAPI.updateNotificationPreferences?.(preferences);
            setMessageType('success');
            setMessage('✓ Preferences saved successfully!');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            setMessageType('error');
            setMessage('✗ Failed to save preferences');
            setTimeout(() => setMessage(''), 3000);
        } finally {
            setSaving(false);
        }
    };

    const handleTestNotification = async (channel) => {
        setTestingSendingChannel(channel);
        try {
            await studentAPI.testSendNotification?.(channel);
            setMessageType('success');
            setMessage(`✓ Test notification sent via ${channel}!`);
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            setMessageType('error');
            setMessage(`✗ Failed to send test notification via ${channel}`);
            setTimeout(() => setMessage(''), 3000);
        } finally {
            setTestingSendingChannel(null);
        }
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.spinner}></div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.content}>
                {/* Header */}
                <div style={styles.header}>
                    <h1 style={styles.title}>🔔 Notification Preferences</h1>
                    <p style={styles.subtitle}>
                        Customize how you receive notifications
                    </p>
                </div>

                {/* Message Toast */}
                {message && (
                    <div style={{
                        ...styles.toast,
                        ...(messageType === 'success' ? styles.toastSuccess : styles.toastError)
                    }}>
                        {message}
                    </div>
                )}

                {/* Notification Channels */}
                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>📨 In-App Notifications</h2>
                    <label style={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            name="in_app_notifications"
                            checked={preferences.in_app_notifications}
                            onChange={handleInputChange}
                            style={styles.checkbox}
                        />
                        <span>Enable in-app notifications</span>
                    </label>
                    <p style={styles.helperText}>
                        Show notifications in the notification center and bell icon
                    </p>
                </div>

                {/* Email Configuration */}
                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>📧 Email Notifications</h2>
                    <label style={styles.label}>
                        <span>Email Address</span>
                        <input
                            type="email"
                            name="email"
                            value={preferences.email}
                            onChange={handleInputChange}
                            placeholder="your@example.com"
                            style={styles.input}
                        />
                    </label>
                    <label style={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            name="email_notifications"
                            checked={preferences.email_notifications}
                            onChange={handleInputChange}
                            style={styles.checkbox}
                        />
                        <span>Enable email notifications</span>
                    </label>
                    <button
                        onClick={() => handleTestNotification('email')}
                        disabled={testingSendingChannel === 'email' || !preferences.email}
                        style={{
                            ...styles.testButton,
                            ...(testingSendingChannel === 'email' ? styles.testButtonLoading : {})
                        }}
                    >
                        {testingSendingChannel === 'email' ? '⏳ Sending...' : '📧 Send Test Email'}
                    </button>
                    <p style={styles.helperText}>
                        Learn about achievements, streaks, new courses, and more
                    </p>
                </div>

                {/* WhatsApp Configuration */}
                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>💬 WhatsApp Notifications</h2>
                    <label style={styles.label}>
                        <span>Phone Number (with country code)</span>
                        <input
                            type="tel"
                            name="phone"
                            value={preferences.phone}
                            onChange={handleInputChange}
                            placeholder="+1234567890"
                            style={styles.input}
                        />
                    </label>
                    <label style={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            name="whatsapp_enabled"
                            checked={preferences.whatsapp_enabled}
                            onChange={handleInputChange}
                            style={styles.checkbox}
                        />
                        <span>Enable WhatsApp notifications</span>
                    </label>
                    <button
                        onClick={() => handleTestNotification('whatsapp')}
                        disabled={testingSendingChannel === 'whatsapp' || !preferences.phone}
                        style={{
                            ...styles.testButton,
                            ...(testingSendingChannel === 'whatsapp' ? styles.testButtonLoading : {})
                        }}
                    >
                        {testingSendingChannel === 'whatsapp' ? '⏳ Sending...' : '💬 Send Test Message'}
                    </button>
                    <p style={styles.helperText}>
                        Get instant reminders and motivational messages on WhatsApp
                    </p>
                </div>

                {/* Notification Types */}
                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>📋 Notification Types</h2>
                    <div style={styles.notificationTypes}>
                        {[
                            { icon: '🔥', name: 'Streak Reminders', desc: 'Daily motivation to keep your streak alive' },
                            { icon: '🏆', name: 'Achievements', desc: 'When you unlock new badges and milestones' },
                            { icon: '📚', name: 'Course Updates', desc: 'New courses, lessons, and course enrollment' },
                            { icon: '🎉', name: 'Milestones', desc: 'Level ups, learning goals, and celebrations' },
                            { icon: '👥', name: 'Leaderboard', desc: 'Rank changes and friend activities' },
                            { icon: '📢', name: 'Announcements', desc: 'Important platform updates' }
                        ].map((type, idx) => (
                            <div key={idx} style={styles.notificationType}>
                                <div style={styles.typeIcon}>{type.icon}</div>
                                <div style={styles.typeInfo}>
                                    <h4 style={styles.typeName}>{type.name}</h4>
                                    <p style={styles.typeDesc}>{type.desc}</p>
                                </div>
                                <div style={styles.typeStatus}>✓</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Save Button */}
                <div style={styles.actions}>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        style={{
                            ...styles.saveButton,
                            ...(saving ? styles.saveButtonLoading : {})
                        }}
                    >
                        {saving ? '💾 Saving...' : '💾 Save Preferences'}
                    </button>
                </div>

                {/* Info Box */}
                <div style={styles.infoBox}>
                    <div style={{ fontSize: '16px', marginBottom: '10px' }}>ℹ️ About Notifications</div>
                    <ul style={styles.infoList}>
                        <li><strong>In-App:</strong> Instant notifications in your dashboard</li>
                        <li><strong>Email:</strong> Daily digest of activities (opt-in)</li>
                        <li><strong>WhatsApp:</strong> Instant reminders for streaks and milestones</li>
                        <li><strong>Frequency:</strong> We respect your preferences - no spam!</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: {
        minHeight: '100vh',
        background: '#f9fafb',
        padding: '40px 20px',
    },
    content: {
        maxWidth: '700px',
        margin: '0 auto',
    },
    header: {
        marginBottom: '40px',
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#1f2937',
        margin: 0,
    },
    subtitle: {
        fontSize: '16px',
        color: '#6b7280',
        marginTop: '8px',
    },
    section: {
        background: 'white',
        borderRadius: '12px',
        padding: '25px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    },
    sectionTitle: {
        fontSize: '18px',
        fontWeight: '700',
        color: '#1f2937',
        marginBottom: '18px',
        margin: 0,
    },
    label: {
        display: 'block',
        marginBottom: '12px',
    },
    checkboxLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '15px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        color: '#374151',
    },
    checkbox: {
        width: '18px',
        height: '18px',
        cursor: 'pointer',
        accentColor: '#27b05b',
    },
    input: {
        width: '100%',
        padding: '10px 12px',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        fontSize: '14px',
        fontFamily: 'inherit',
        marginTop: '6px',
    },
    helperText: {
        fontSize: '12px',
        color: '#9ca3af',
        marginTop: '8px',
        marginBottom: '12px',
    },
    testButton: {
        padding: '10px 16px',
        background: '#f3f4f6',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'all 0.2s',
        marginBottom: '10px',
    },
    testButtonLoading: {
        background: '#e5e7eb',
        cursor: 'not-allowed',
        color: '#6b7280',
    },
    notificationTypes: {
        display: 'grid',
        gap: '12px',
    },
    notificationType: {
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        padding: '15px',
        background: '#f9fafb',
        borderRadius: '8px',
        border: '1px solid #f0f0f0',
    },
    typeIcon: {
        fontSize: '24px',
        minWidth: '32px',
    },
    typeInfo: {
        flex: 1,
    },
    typeName: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#374151',
        margin: 0,
    },
    typeDesc: {
        fontSize: '12px',
        color: '#6b7280',
        margin: '4px 0 0 0',
    },
    typeStatus: {
        fontSize: '18px',
        color: '#27b05b',
    },
    actions: {
        display: 'flex',
        gap: '12px',
        marginTop: '30px',
    },
    saveButton: {
        flex: 1,
        padding: '12px 24px',
        background: '#27b05b',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    saveButtonLoading: {
        background: '#1f8f4a',
        cursor: 'not-allowed',
        opacity: 0.8,
    },
    infoBox: {
        background: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: '8px',
        padding: '20px',
        marginTop: '30px',
        color: '#1e40af',
        fontSize: '13px',
    },
    infoList: {
        margin: '12px 0 0 0',
        paddingLeft: '20px',
    },
    toast: {
        padding: '12px 16px',
        borderRadius: '8px',
        marginBottom: '20px',
        fontSize: '13px',
        fontWeight: '500',
        animation: 'slideIn 0.3s ease',
    },
    toastSuccess: {
        background: '#d1fae5',
        color: '#065f46',
        border: '1px solid #6ee7b7',
    },
    toastError: {
        background: '#fee2e2',
        color: '#991b1b',
        border: '1px solid #fca5a5',
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '4px solid #f0f0f0',
        borderTop: '4px solid #27b05b',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '100px auto',
    }
};

// Add animations
const addAnimations = () => {
    if (!document.getElementById('notification-prefs-animations')) {
        const style = document.createElement('style');
        style.id = 'notification-prefs-animations';
        style.textContent = `
            @keyframes spin {
                to { transform: rotate(360deg); }
            }
            @keyframes slideIn {
                from { transform: translateY(-10px); opacity: 0; }
                to { transform: translateY(0); opacity: 1; }
            }
        `;
        document.head.appendChild(style);
    }
};

addAnimations();

export default NotificationPreferences;
